"use strict";Object.defineProperty(exports, "__esModule", {value: true});const BASE83 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz#$%*+,-.:;=?@[]^_{|}~'
 const BLURHASH_CACHE_CAPACITY = 256; exports.BLURHASH_CACHE_CAPACITY = BLURHASH_CACHE_CAPACITY
const memo = new Map()

const decode83 = (value) => {
  let result = 0
  for (const character of value) {
    const digit = BASE83.indexOf(character)
    if (digit < 0) return null
    result = result * 83 + digit
  }
  return result
}

const srgbToLinear = (value) => {
  const normalized = value / 255
  return normalized <= 0.04045
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4)
}

const linearToSrgb = (value) => {
  const clamped = Math.max(0, Math.min(1, value))
  return Math.round(255 * (clamped <= 0.0031308
    ? clamped * 12.92
    : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055))
}

const signedSquare = (value) => Math.sign(value) * value * value

const decodePixels = (hash, width, height) => {
  const sizeFlag = decode83(hash.slice(0, 1))
  if (sizeFlag === null) return null
  const componentX = sizeFlag % 9 + 1
  const componentY = Math.floor(sizeFlag / 9) + 1
  if (hash.length !== 4 + 2 * componentX * componentY) return null

  const quantizedMaximum = decode83(hash.slice(1, 2))
  const dc = decode83(hash.slice(2, 6))
  if (quantizedMaximum === null || dc === null) return null
  const maximum = (quantizedMaximum + 1) / 166
  const colors = [
    [srgbToLinear(dc >> 16), srgbToLinear((dc >> 8) & 255), srgbToLinear(dc & 255)],
  ]

  for (let index = 1; index < componentX * componentY; index++) {
    const value = decode83(hash.slice(4 + index * 2, 6 + index * 2))
    if (value === null) return null
    colors.push([
      signedSquare((Math.floor(value / (19 * 19)) - 9) / 9) * maximum,
      signedSquare((Math.floor(value / 19) % 19 - 9) / 9) * maximum,
      signedSquare((value % 19 - 9) / 9) * maximum,
    ])
  }

  const pixels = new Uint8Array(width * height * 3)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let red = 0
      let green = 0
      let blue = 0
      for (let componentYIndex = 0; componentYIndex < componentY; componentYIndex++) {
        const basisY = Math.cos(Math.PI * y * componentYIndex / height)
        for (let componentXIndex = 0; componentXIndex < componentX; componentXIndex++) {
          const basis = Math.cos(Math.PI * x * componentXIndex / width) * basisY
          const color = colors[componentXIndex + componentYIndex * componentX]
          red += color[0] * basis
          green += color[1] * basis
          blue += color[2] * basis
        }
      }
      const offset = (y * width + x) * 3
      pixels[offset] = linearToSrgb(red)
      pixels[offset + 1] = linearToSrgb(green)
      pixels[offset + 2] = linearToSrgb(blue)
    }
  }
  return pixels
}

const uint32 = (value) => new Uint8Array([
  (value >>> 24) & 255,
  (value >>> 16) & 255,
  (value >>> 8) & 255,
  value & 255,
])

const concat = (...arrays) => {
  const result = new Uint8Array(arrays.reduce((length, array) => length + array.length, 0))
  let offset = 0
  for (const array of arrays) {
    result.set(array, offset)
    offset += array.length
  }
  return result
}

const crc32 = (bytes) => {
  let crc = 0xffffffff
  for (const byte of bytes) {
    crc ^= byte
    for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1))
  }
  return (crc ^ 0xffffffff) >>> 0
}

const chunk = (type, data) => {
  const typeBytes = new Uint8Array(type.split('').map(character => character.charCodeAt(0)))
  return concat(uint32(data.length), typeBytes, data, uint32(crc32(concat(typeBytes, data))))
}

const adler32 = (bytes) => {
  let first = 1
  let second = 0
  for (const byte of bytes) {
    first = (first + byte) % 65521
    second = (second + first) % 65521
  }
  return ((second << 16) | first) >>> 0
}

const zlibStore = (bytes) => {
  const blocks = [new Uint8Array([0x78, 0x01])]
  for (let offset = 0; offset < bytes.length; offset += 65535) {
    const length = Math.min(65535, bytes.length - offset)
    const final = offset + length === bytes.length ? 1 : 0
    blocks.push(new Uint8Array([
      final,
      length & 255,
      (length >>> 8) & 255,
      (~length) & 255,
      ((~length) >>> 8) & 255,
    ]), bytes.slice(offset, offset + length))
  }
  blocks.push(uint32(adler32(bytes)))
  return concat(...blocks)
}

const encodePng = (pixels, width, height) => {
  const scanlines = new Uint8Array(height * (1 + width * 3))
  for (let row = 0; row < height; row++) {
    const target = row * (1 + width * 3)
    scanlines[target] = 0
    scanlines.set(pixels.slice(row * width * 3, (row + 1) * width * 3), target + 1)
  }
  const ihdr = concat(uint32(width), uint32(height), new Uint8Array([8, 2, 0, 0, 0]))
  return concat(
    new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]),
    chunk('IHDR', ihdr),
    chunk('IDAT', zlibStore(scanlines)),
    chunk('IEND', new Uint8Array()),
  )
}

const toBase64 = (bytes) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
  let encoded = ''
  for (let offset = 0; offset < bytes.length; offset += 3) {
    const first = bytes[offset]
    const hasSecond = offset + 1 < bytes.length
    const hasThird = offset + 2 < bytes.length
    const second = hasSecond ? bytes[offset + 1] : 0
    const third = hasThird ? bytes[offset + 2] : 0
    const value = (first << 16) | (second << 8) | third
    encoded += alphabet[(value >>> 18) & 63]
    encoded += alphabet[(value >>> 12) & 63]
    encoded += hasSecond ? alphabet[(value >>> 6) & 63] : '='
    encoded += hasThird ? alphabet[value & 63] : '='
  }
  return encoded
}

 const decodeBlurhashToDataUri = (
  hash,
  width = 32,
  height = 32,
) => {
  try {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) return null
    const key = `${hash}|${width}|${height}`
    const cached = memo.get(key)
    if (cached) {
      memo.delete(key)
      memo.set(key, cached)
      return cached
    }
    const pixels = decodePixels(hash, width, height)
    if (!pixels) return null
    const uri = `data:image/png;base64,${toBase64(encodePng(pixels, width, height))}`
    memo.set(key, uri)
    if (memo.size > exports.BLURHASH_CACHE_CAPACITY) {
      const oldestKey = memo.keys().next().value
      if (oldestKey !== undefined) memo.delete(oldestKey)
    }
    return uri
  } catch (_) {
    return null
  }
}; exports.decodeBlurhashToDataUri = decodeBlurhashToDataUri
